<template>
  <div class="ticket-inline-select">
    <app-select
      ref="select"
      class="ticket-inline-select__control"
      :value="value"
      design="new"
      :options="options"
      :multiple="multiple"
      :label="label"
      :taggable="canCreate"
      :push-tags="false"
      :create-option="createOption"
      :append-to-body="true"
      :calculate-position="positionDropdown"
      :placeholder="placeholder"
      :disabled="working"
      @input="handleInput"
      @keydown.esc.native="$emit('cancel')"
    >
      <template #custom-option="option">
        <span v-if="option.__create__" class="ticket-inline-option ticket-inline-option--create">
          <span aria-hidden="true">+</span>
          Create “{{ option.name }}”
        </span>
        <template v-else>
          <i
            v-if="option.color"
            class="ticket-inline-option__dot"
            :style="{ backgroundColor: option.color }"
          ></i>
          <span>{{ option[label] }}</span>
        </template>
      </template>
    </app-select>
  </div>
</template>

<script>
import { createPopper } from '@popperjs/core'
import { postSupportTicketDictionary } from '@/http'
import { apiError } from '../ticketState'
import { SUPPORT_TAG_COLORS } from './tagPalette'

export default {
  name: 'SupportTicketInlineSelect',
  props: {
    value: { default: null },
    options: { type: Array, default: () => [] },
    multiple: Boolean,
    label: { type: String, default: 'name' },
    placeholder: { type: String, default: 'Select' },
    canCreate: Boolean,
    kind: { type: String, default: '' },
    parentPayload: { type: Object, default: () => ({}) }
  },
  data() { return { working: false } },
  mounted() {
    document.addEventListener('keydown', this.handleEscape)
    this.$nextTick(() => {
      const input = this.$refs.select?.$el.querySelector('[role="combobox"]')
      if (input) input.focus()
    })
  },
  beforeDestroy() { document.removeEventListener('keydown', this.handleEscape) },
  methods: {
    handleEscape(event) {
      if (event.key === 'Escape') this.$emit('cancel')
    },
    positionDropdown(dropdownList, component, { width }) {
      dropdownList.classList.add('support-ticket-inline-options')
      dropdownList.style.width = `${Math.max(parseFloat(width) || 0, 240)}px`
      dropdownList.style.zIndex = '10020'

      const popper = createPopper(component.$refs.toggle, dropdownList, {
        placement: 'bottom-start',
        modifiers: [
          { name: 'offset', options: { offset: [0, 6] } },
          { name: 'flip', options: { fallbackPlacements: ['top-start'] } },
          { name: 'preventOverflow', options: { padding: 12 } }
        ]
      })

      return () => popper.destroy()
    },
    createOption(name) { return { name: (name || '').trim(), color: SUPPORT_TAG_COLORS[7], __create__: true } },
    handleInput(value) {
      const values = this.multiple ? (value || []) : [value]
      const draft = values.find(item => item?.__create__)
      if (!draft) { this.$emit('input', value); return }
      this.create(draft, values.filter(item => !item?.__create__))
    },
    async create(draft, selected) {
      if (!this.kind || !draft.name || this.working) return
      this.working = true
      try {
        const response = await postSupportTicketDictionary(this.kind, {
          name: draft.name,
          color: draft.color,
          ...this.parentPayload
        })
        this.$emit('created', { kind: this.kind, item: response.data })
        this.$emit('input', this.multiple ? [...selected, response.data] : response.data)
      } catch (error) {
        this.$notify({ type: 'error', text: apiError(error) })
      } finally {
        this.working = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.ticket-inline-select { position: relative; z-index: 8; width: 100%; min-width: 0; }
.ticket-inline-select :deep(.vs__dropdown-toggle) { min-height: 36px; height: 36px; border-radius: 9px; }
.ticket-inline-select :deep(.select--multiple .vs__dropdown-toggle) { height: auto; min-height: 36px; }
.ticket-inline-select :deep(.vs--open .vs__dropdown-toggle) { border-radius: 9px; box-shadow: 0 0 0 3px rgba($ui-blue, .1); }
.ticket-inline-select i { width: 9px; height: 9px; flex: 0 0 9px; margin-right: 7px; border-radius: 50%; }
</style>

<style lang="scss">
.vs__dropdown-menu.support-ticket-inline-options[data-popper-placement] {
  max-height: 260px;
  padding: $ui-space-4;
  border: 1px solid $ui-line-strong;
  border-radius: 10px;
  background: $ui-surface;
  box-shadow: 0 12px 30px rgba(23, 36, 58, .16);
  color: $ui-text;
  font-family: $ui-font-body;
  scrollbar-color: $ui-blue transparent;
  scrollbar-width: thin;
}

.vs__dropdown-menu.support-ticket-inline-options[data-popper-placement] .vs__dropdown-option {
  display: flex;
  min-height: 38px;
  align-items: center;
  margin: 1px 0;
  padding: $ui-space-8 $ui-space-12;
  border-radius: 8px;
  color: $ui-text;
  font-size: $ui-font-size-body;
  transition: background-color .15s ease, color .15s ease;
}

.vs__dropdown-menu.support-ticket-inline-options[data-popper-placement] .select__item {
  width: 100%;
  color: inherit;
  font-family: $ui-font-body;
  font-size: $ui-font-size-body;
  font-weight: 500;
}

.vs__dropdown-menu.support-ticket-inline-options[data-popper-placement] .vs__dropdown-option.vs__dropdown-option--highlight {
  background: $ui-blue-soft;
  color: $ui-blue;
}

.vs__dropdown-menu.support-ticket-inline-options[data-popper-placement] .vs__dropdown-option.vs__dropdown-option--highlight .select__item {
  color: $ui-blue;
}

.vs__dropdown-menu.support-ticket-inline-options[data-popper-placement] .vs__dropdown-option.vs__dropdown-option--selected:not(.vs__dropdown-option--highlight) {
  background: $ui-surface-soft;
  color: $ui-ink;
  font-weight: 600;
}

.vs__dropdown-menu.support-ticket-inline-options[data-popper-placement] .vs__dropdown-option.vs__dropdown-option--selected:not(.vs__dropdown-option--highlight) .select__item {
  color: $ui-ink;
  font-weight: 600;
}

.vs__dropdown-menu.support-ticket-inline-options[data-popper-placement] .vs__dropdown-option.vs__dropdown-option--selected::after {
  content: "\2713";
  margin-left: auto;
  padding-left: $ui-space-8;
  color: $ui-blue;
  font-size: 13px;
  font-weight: 700;
}

.vs__dropdown-menu.support-ticket-inline-options[data-popper-placement] .ticket-inline-option {
  display: inline-flex;
  align-items: center;
  gap: $ui-space-8;
}

.vs__dropdown-menu.support-ticket-inline-options[data-popper-placement] .ticket-inline-option--create { color: $ui-blue; }
.vs__dropdown-menu.support-ticket-inline-options[data-popper-placement] .ticket-inline-option--create > span { font-size: 18px; line-height: 1; }
.vs__dropdown-menu.support-ticket-inline-options[data-popper-placement] .ticket-inline-option__dot { width: 10px; height: 10px; flex: 0 0 10px; margin-right: $ui-space-8; border-radius: 50%; }
</style>
